import { type KeyboardEvent, useEffect, useMemo, useState } from 'react';

import { contemBusca } from '@/lib/texto';

export interface OpcaoPesquisavel {
  valor: string;
  rotulo: string;
  /** Texto auxiliar à direita (sigla, documento); não entra no nome acessível. */
  detalhe?: string;
  /** Cabeçalho do grupo ("Desta empresa"): opções do mesmo grupo vêm juntas na lista. */
  grupo?: string;
}

export type BuscarOpcoes = (
  termo: string,
  signal: AbortSignal,
) => Promise<readonly OpcaoPesquisavel[]>;

export type FonteOpcoes =
  | { tipo: 'local'; opcoes: readonly OpcaoPesquisavel[] }
  | { tipo: 'remoto'; buscar: BuscarOpcoes; minCaracteres: number; atrasoMs: number };

export type EstadoBusca = 'pronto' | 'carregando' | 'curto' | 'erro';

/**
 * Opções visíveis para o termo. Local: filtra no cliente. Remoto: espera o usuário parar de
 * digitar (debounce), cancela a requisição anterior e só busca a partir de `minCaracteres`.
 */
export function useOpcoesPesquisaveis(fonte: FonteOpcoes, termo: string, aberto: boolean) {
  const [resposta, setResposta] = useState<Resposta | null>(null);
  const [tentativa, setTentativa] = useState(0);
  const buscar = fonte.tipo === 'remoto' ? fonte.buscar : null;
  const minCaracteres = fonte.tipo === 'remoto' ? fonte.minCaracteres : 0;
  const atrasoMs = fonte.tipo === 'remoto' ? fonte.atrasoMs : 0;
  const curto = termo.trim().length < minCaracteres;
  const chave = `${termo.trim()}|${String(tentativa)}`;

  useEffect(() => {
    if (!buscar || !aberto || curto) return undefined;
    const controle = new AbortController();
    const relogio = setTimeout(() => {
      buscar(termo.trim(), controle.signal).then(
        (opcoes) => {
          if (!controle.signal.aborted) setResposta({ chave, opcoes, erro: false });
        },
        () => {
          if (!controle.signal.aborted) setResposta({ chave, opcoes: [], erro: true });
        },
      );
    }, atrasoMs);
    return () => {
      clearTimeout(relogio);
      controle.abort();
    };
  }, [buscar, atrasoMs, termo, aberto, curto, chave]);

  const opcoes = useMemo(
    () =>
      fonte.tipo === 'local'
        ? fonte.opcoes.filter((o) => contemBusca(o.rotulo, termo))
        : (resposta?.opcoes ?? []),
    [fonte, termo, resposta],
  );
  return {
    opcoes,
    estado: estadoDaBusca(fonte.tipo, curto, resposta, chave),
    tentarDeNovo: () => {
      setTentativa((t) => t + 1);
    },
  };
}

interface Resposta {
  chave: string;
  opcoes: readonly OpcaoPesquisavel[];
  erro: boolean;
}

/** Estado derivado (sem setState no efeito): a resposta sabe a qual termo pertence. */
function estadoDaBusca(
  tipo: FonteOpcoes['tipo'],
  curto: boolean,
  resposta: Resposta | null,
  chave: string,
): EstadoBusca {
  if (tipo === 'local') return 'pronto';
  if (curto) return 'curto';
  if (resposta?.chave !== chave) return 'carregando';
  return resposta.erro ? 'erro' : 'pronto';
}

/** Valor exibido: local procura na lista; remoto usa a última escolha ou `opcaoSelecionada`. */
export function opcaoAtual(
  valor: string | null,
  fonte: { opcoes?: readonly OpcaoPesquisavel[]; opcaoSelecionada?: OpcaoPesquisavel | null },
  escolhida: OpcaoPesquisavel | null,
): OpcaoPesquisavel | null {
  if (valor === null) return null;
  if (fonte.opcoes) return fonte.opcoes.find((o) => o.valor === valor) ?? null;
  if (escolhida?.valor === valor) return escolhida;
  return fonte.opcaoSelecionada?.valor === valor ? fonte.opcaoSelecionada : null;
}

/** ↓/↑ movem a opção ativa (sem passar das pontas); Enter escolhe. */
export function teclaDaLista(
  e: KeyboardEvent,
  ativa: number,
  total: number,
): { ativa: number } | { escolher: number } | null {
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault();
    const passo = e.key === 'ArrowDown' ? 1 : -1;
    return { ativa: Math.min(Math.max(ativa + passo, 0), total - 1) };
  }
  if (e.key === 'Enter' && ativa >= 0) {
    e.preventDefault();
    return { escolher: ativa };
  }
  return null;
}
